<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('serviceRequest.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('serviceRequest.pageTitle'),
          href: '/admin/service-requests'
        },
        {
          text: $t('create'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-p-2 ts-rounded-md">
          <div class="ts-grid ts-grid-cols-3 ts-gap-5">
            <div class="ts-w-full ts-h-full ts-border-2 ts-border-dashed ts-rounded-md ts-p-3 ts-relative">
              <Connector />
              <BasicInfo v-model="model" :validate="errors" />
            </div>
            <div class="ts-w-full ts-h-full ts-border-2 ts-border-dashed ts-rounded-md ts-p-3 ts-relative">
              <Connector />
              <Client v-model="model" :validate="errors" />
            </div>
            <div class="ts-w-full ts-h-full ts-border-2 ts-border-dashed ts-rounded-md ts-p-3 ts-relative">
              <Technician v-model="model" :validate="errors" />
            </div>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-12 ts-flex ts-items-center ts-justify-end ts-space-x-3">
            <ts-button :disabled="loading" @click.prevent="$router.push({ name: 'service-request' })">{{ $t('cancel') }}
            </ts-button>
            <ts-button :disabled="loading" color="primary" @click.prevent="onUpdate">
              {{ $t('update') }}</ts-button>
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
    <Spin size="large" fix v-if="loading"></Spin>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import Connector from './components/form/connector.vue'
import BasicInfo from './components/form/basic-info.vue'
import Client from './components/form/client.vue'
import Technician from './components/form/technician.vue'
import PlannedMaintenance from './components/form/planned-maintenance.vue'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import { isEmpty, debounce } from 'lodash'

export default {
  components: {
    Connector,
    BasicInfo,
    Client,
    Technician,
    PlannedMaintenance
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      waiting: false,
      model: {
        service_request_id: null,
        service_request_no: null,
        service_request_date: moment().format('DD-MM-YYYY'),
        service_request_type_id: null,
        fee_type_id: null,
        requested_by: null,
        request_photo: null,
        service_status: 0,
        required_spare_part: false,
        input_source: 'W',
        product_id: null,
        stock_out_id: null,
        serial_no: null,
        imei: null,
        owner: null,
        remarks: null,
        comment: null,
        order_kads: null,
        informant: null,
        province_code: null,
        district_code: null,
        commune_code: null,
        village_code: null,
        plate_number: null,
        technician1: null,
        technician2: null,
        contact_number: null,
        open_date: null,
        close_date: null,
        planned_maintenance: [],
        planned_start_date: null,
        planned_finish_date: null
      }
    }
  },
  computed: {
    ...mapState('serviceManagement/serviceRequest', [
      'formViewDatas',
      'edit_data'
    ])
  },
  methods: {
    ...mapActions('serviceManagement/serviceRequest', [
      'getFormViewModel',
      'find',
      'update'
    ]),
    async getPreData() {
      this.loading = true
      await this.find(this.$route.params.id).then(() => {
        this.setEditData()
      })
      await this.getFormViewModel({
        index: undefined,
        params: {
          search_customer: this.model.requested_by,
          customer_id: this.model.requested_by,
          stock_out_id: this.model.stock_out_id,
          product_id: [this.model.product_id],
          province_code: this.model.province_code,
          district_code: this.model.district_code,
          commune_code: this.model.commune_code
        }
      })
      this.model.owner = this.$store.state.authUser.user.user_id
      this.errors = new Errors()
      this.loading = false
    },
    onUpdate() {
      this.loading = true
      this.update({
        id: this.edit_data.service_request_id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'service-request' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearInput() {
      this.model.service_request_id = null
      this.model.service_request_no = null
      this.model.service_request_date = moment().format('DD-MM-YYYY')
      this.model.service_request_type_id = null
      this.model.fee_type_id = null
      this.model.requested_by = null
      this.model.request_photo = null
      this.model.service_status = 0
      this.model.required_spare_part = false
      this.model.input_source = 'W'
      this.model.product_id = null
      this.model.stock_out_id = null
      this.model.serial_no = null
      this.model.imei = null
      this.model.owner = null
      this.model.remarks = null
      this.model.comment = null

      this.model.order_kads = null
      this.model.informant = null
      this.model.province_code = null
      this.model.district_code = null
      this.model.commune_code = null
      this.model.village_code = null
      this.model.plate_number = null
      this.model.technician1 = null
      this.model.technician2 = null
      this.model.contact_number = null
      this.model.open_date = null
      this.model.close_date = null
      this.model.planned_start_date = null
      this.model.planned_finish_date = null

      this.model.planned_maintenance = null
      this.$store.commit('serviceManagement/serviceRequest/SET_EDIT_DATA', {})
    },
    async setEditData() {
      if (!isEmpty(this.edit_data)) {
        this.model.service_request_id = this.edit_data.service_request_id
        this.model.service_request_no = this.edit_data.service_request_no
        this.model.service_request_date = this.edit_data.service_request_date
        this.model.service_request_type_id = this.edit_data.service_request_type_id
        this.model.fee_type_id = this.edit_data.fee_type_id
        this.model.requested_by = this.edit_data.requested_by
        this.model.request_photo = this.edit_data.request_photo
        this.model.service_status = this.edit_data.service_status
        this.model.required_spare_part = this.edit_data.required_spare_part
        this.model.input_source = this.edit_data.input_source
        this.model.product_id = this.edit_data.product_id
        this.model.stock_out_id = this.edit_data.stock_out_id
        this.model.serial_no = this.edit_data.serial_no
        this.model.imei = this.edit_data.imei
        this.model.owner = this.edit_data.owner
        this.model.remarks = this.edit_data.remarks
        this.model.comment = this.edit_data.comment
        this.model.order_kads = this.edit_data.order_kads
        this.model.informant = this.edit_data.informant
        this.model.province_code = this.edit_data.province_code
        this.model.district_code = this.edit_data.district_code
        this.model.commune_code = this.edit_data.commune_code
        this.model.village_code = this.edit_data.village_code
        this.model.plate_number = this.edit_data.plate_number
        this.model.technician1 = this.edit_data.technician1
        this.model.technician2 = this.edit_data.technician2
        this.model.contact_number = this.edit_data.contact_number
        this.model.open_date = this.edit_data.open_date ? moment(this.edit_data.open_date, "DD-MM-YYYY hh:mm:ss A").format("DD-MM-YYYY HH:mm:ss") : null
        this.model.close_date = this.edit_data.close_date ? moment(this.edit_data.close_date, "DD-MM-YYYY hh:mm:ss A").format("DD-MM-YYYY HH:mm:ss") : null
        this.model.planned_start_date = this.edit_data.planned_start_date ? moment(this.edit_data.planned_start_date, "DD-MM-YYYY hh:mm:ss A").format("DD-MM-YYYY HH:mm:ss") : null
        this.model.planned_finish_date = this.edit_data.planned_finish_date ? moment(this.edit_data.planned_finish_date, "DD-MM-YYYY hh:mm:ss A").format("DD-MM-YYYY HH:mm:ss") : null
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getPreData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('serviceManagement/serviceRequest/SET_EDIT_DATA', {})
    next()
  }
}
</script>
<style scoped>
* {
  font-family: Roboto, Khmer, 'Font Awesome 5 Free';
}
</style>
